import React, { useMemo, useState } from 'react'
import Container from '@embracesbs/component-container'
import Icon from '@embracesbs/component-icon'
import Text from '@embracesbs/component-text'
import TextField from '@embracesbs/component-textfield'
import Table from '@embracesbs/component-table'
import TableRow from '@embracesbs/component-tablerow'
import EmptyState from '@embracesbs/component-emptystate'

interface KrrAllocationValue {
  value?: number | string | null
  severity?: string
}

type KrrAllocation = KrrAllocationValue | number | string | null | undefined

interface KrrAllocations {
  requests?: { cpu?: KrrAllocation; memory?: KrrAllocation }
  limits?: { cpu?: KrrAllocation; memory?: KrrAllocation }
}

interface KrrPod {
  name?: string
  deleted?: boolean
}

interface KrrObject {
  namespace?: string
  name?: string
  kind?: string
  container?: string
  allocations?: KrrAllocations
  pods?: KrrPod[]
}

interface KrrScan {
  object: KrrObject
  recommended?: KrrAllocations
  severity?: string
}

interface KrrReport {
  scans?: KrrScan[]
  score?: number
  strategy?: { name?: string }
  errors?: unknown[]
}

const SEVERITY_ORDER: Record<string, number> = {
  CRITICAL: 0,
  WARNING: 1,
  UNKNOWN: 2,
  OK: 3,
  GOOD: 4
}

const fieldSeverity = (allocation: KrrAllocation): string | null =>
  typeof allocation === 'object' && allocation?.severity ? allocation.severity.toUpperCase() : null

type Direction = 'over' | 'under' | null

interface SeverityField {
  severity: string
  current: KrrAllocation
  recommended: KrrAllocation
}

// KRR's raw JSON already carries a severity per field (request/limit x cpu/memory), and
// the row-level `severity` we get is just the worst of those four. The CPU limit field is
// structurally useless for that: KRR's "simple" strategy never recommends a CPU limit at
// all, so that field sits at WARNING (or GOOD only when there's no current limit either)
// no matter how well-sized everything else is. Pick the worst of the other three fields
// only, so a row that's spot-on everywhere but missing a CPU limit reads as such — and
// keep the current/recommended pair around so direction (over vs under) can be derived
// from the same field the severity came from.
const worstField = (scan: KrrScan): SeverityField | null => {
  const fields = [
    { current: scan.object?.allocations?.requests?.cpu, recommended: scan.recommended?.requests?.cpu },
    { current: scan.object?.allocations?.requests?.memory, recommended: scan.recommended?.requests?.memory },
    { current: scan.object?.allocations?.limits?.memory, recommended: scan.recommended?.limits?.memory }
  ]
    .map(({ current, recommended }) => {
      const severity = fieldSeverity(recommended)
      return severity ? { severity, current, recommended } : null
    })
    .filter((value): value is SeverityField => value !== null)

  if (fields.length === 0) return null

  return fields.reduce((worst, field) => ((SEVERITY_ORDER[field.severity] ?? 99) < (SEVERITY_ORDER[worst.severity] ?? 99) ? field : worst))
}

const getSeverity = (scan: KrrScan): string => worstField(scan)?.severity ?? (scan.severity ?? 'UNKNOWN').toUpperCase()

// Whether the worst field is over- or under-provisioned relative to KRR's recommendation.
// A missing current value (no request/limit set at all) counts as 0, i.e. under-provisioned.
const getDirection = (scan: KrrScan): Direction => {
  const field = worstField(scan)
  if (!field) return null
  const recommended = unwrap(field.recommended)
  if (recommended === null) return null
  const current = unwrap(field.current) ?? 0
  if (current === recommended) return null
  return current > recommended ? 'over' : 'under'
}

const severityColor = (severity: string): string => {
  switch (severity.toUpperCase()) {
    case 'CRITICAL':
      return 'var(--tma-status-failed)'
    case 'WARNING':
      return 'var(--tma-status-wait)'
    case 'OK':
    case 'GOOD':
      return 'var(--tma-status-ok)'
    default:
      return 'var(--tma-status-unknown)'
  }
}

// KRR's severity is just the size of the gap between current allocation and its
// recommendation — it says nothing about which side of that gap the workload is on, so
// direction (over vs under-provisioned) is derived separately, from the same current/
// recommended pair, and passed in. Pass direction=null for a severity tier in the
// abstract (e.g. the filter buttons, which group rows on both sides of the gap).
const severityLabel = (severity: string, direction: Direction): string => {
  const magnitude = severity.toUpperCase() === 'CRITICAL' ? 'Significantly' : severity.toUpperCase() === 'WARNING' ? 'Slightly' : null

  // Per-row badges have a fixed-width column and already carry direction via an arrow
  // icon (see the 'severity' column's render) and magnitude via color, so drop the
  // magnitude word there — it's what was pushing "Significantly over-provisioned" past
  // the column width. The tier-only case (direction=null, e.g. the filter buttons,
  // which aren't width-constrained and group both directions) keeps the full wording.
  if (magnitude) return direction ? `${direction === 'over' ? 'Over' : 'Under'}-provisioned` : `${magnitude} mis-sized`

  switch (severity.toUpperCase()) {
    case 'OK':
      return 'Nearly right-sized'
    case 'GOOD':
      return 'Right-sized'
    default:
      return 'No data'
  }
}

const severityDescription = (severity: string, direction: Direction): string => {
  const relation = direction === 'over' ? 'is above' : direction === 'under' ? 'is below' : 'differs from'
  switch (severity.toUpperCase()) {
    case 'CRITICAL':
      return `Current allocation ${relation} actual usage by a large amount (500Mi+ memory or 0.5+ CPU cores).`
    case 'WARNING':
      return `Current allocation ${relation} actual usage by a moderate amount (250-500Mi memory or 0.25-0.5 CPU cores).`
    case 'OK':
      return `Current allocation ${relation} actual usage by a small amount (100-250Mi memory or 0.1-0.25 CPU cores).`
    case 'GOOD':
      return 'Current allocation closely matches actual usage.'
    default:
      return 'Not enough Prometheus usage data was available to calculate a recommendation.'
  }
}

const unwrap = (allocation: KrrAllocation): number | null => {
  if (allocation === null || allocation === undefined) return null
  if (typeof allocation === 'object') return unwrap(allocation.value)
  const parsed = Number(allocation)
  return Number.isFinite(parsed) ? parsed : null
}

const trimDecimal = (value: number): string => {
  const rounded = Math.round(value * 10) / 10
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1)
}

const formatCpu = (allocation: KrrAllocation): string => {
  const cores = unwrap(allocation)
  if (cores === null) return '–'
  const millicores = Math.round(cores * 1000)
  return millicores < 1000 ? `${millicores}m` : trimDecimal(millicores / 1000)
}

const formatMemory = (allocation: KrrAllocation): string => {
  const bytes = unwrap(allocation)
  if (bytes === null) return '–'
  const mebibytes = bytes / (1024 * 1024)
  return mebibytes >= 1024 ? `${trimDecimal(mebibytes / 1024)}Gi` : `${trimDecimal(mebibytes)}Mi`
}

// When the (already-rounded) current and recommended values print identically, an
// arrow pointing from a value to itself is just noise — show the single value instead.
const formatFlow = (current: string, recommended: string): React.ReactNode =>
  current === recommended ? (
    <span>{current}</span>
  ) : (
    <>
      {current} → {recommended}
    </>
  )

// Namespace already carries most of the name, and container is usually the same
// as (or a short suffix of) the workload name — fold kind/container in as context
// instead of three separate name-shaped columns.
const formatWorkload = (object?: KrrObject): { primary: string; suffix: string } => {
  const primary = object?.name ?? '–'
  const extras = [
    object?.kind && object.kind !== 'Deployment' ? object.kind : null,
    object?.container && object.container !== primary ? object.container : null
  ].filter((value): value is string => !!value)
  return { primary, suffix: extras.join(' · ') }
}

interface KrrColumn {
  key: string
  header: string
  tooltip?: string
  style: Record<string, unknown>
  render: (scan: KrrScan) => React.ReactNode
  sortValue?: (scan: KrrScan) => number | string
}

const COLUMNS: KrrColumn[] = [
  {
    key: 'namespace',
    header: 'Namespace',
    style: { width: 260, leftSpacing: 16 },
    sortValue: (scan) => scan.object?.namespace ?? '',
    render: (scan) => (
      <Text>
        <span data-tooltip-id='global-tooltip' data-tooltip-content={scan.object?.namespace}>
          {scan.object?.namespace ?? '–'}
        </span>
      </Text>
    )
  },
  {
    key: 'workload',
    header: 'Workload',
    style: { width: 380, leftSpacing: 16 },
    sortValue: (scan) => scan.object?.name ?? '',
    render: (scan) => {
      const { primary, suffix } = formatWorkload(scan.object)
      const fullText = suffix ? `${primary} (${suffix})` : primary
      return (
        <Text>
          <span data-tooltip-id='global-tooltip' data-tooltip-content={fullText}>
            {primary}
            {suffix && <span style={{ color: 'var(--tma-text-muted)', marginLeft: '6px', fontSize: '12px' }}>{suffix}</span>}
          </span>
        </Text>
      )
    }
  },
  {
    key: 'severity',
    header: 'Severity',
    tooltip: "How far the current CPU/memory allocation is from KRR's recommendation — not an indicator of an active problem.",
    style: { width: 190, leftSpacing: 16 },
    sortValue: (scan) => SEVERITY_ORDER[getSeverity(scan)] ?? 99,
    render: (scan) => {
      const severity = getSeverity(scan)
      const direction = getDirection(scan)
      const color = severityColor(severity)
      return (
        <Text style={{ color, fontWeight: 600, fontSize: '13px' }}>
          <span
            data-tooltip-id='global-tooltip'
            data-tooltip-content={severityDescription(severity, direction)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            {direction && <Icon iconName={direction === 'over' ? 'ArrowUp1' : 'ArrowDown1'} color={color} size={12} />}
            {severityLabel(severity, direction)}
          </span>
        </Text>
      )
    }
  },
  {
    key: 'replicas',
    header: 'Samples',
    tooltip: 'Pods sampled to build this recommendation, including ones since replaced during the scan window.',
    style: { width: 100, leftSpacing: 16 },
    sortValue: (scan) => scan.object?.pods?.length ?? 0,
    render: (scan) => (
      <Text>
        <span
          data-tooltip-id='global-tooltip'
          data-tooltip-content='Pods sampled to build this recommendation, including ones since replaced during the scan window.'
        >
          {scan.object?.pods?.length ?? 0}
        </span>
      </Text>
    )
  },
  {
    key: 'cpuRequest',
    header: 'CPU request',
    tooltip: 'CPU request (current → recommended)',
    style: { width: 130, leftSpacing: 16 },
    sortValue: (scan) => unwrap(scan.object?.allocations?.requests?.cpu) ?? -1,
    render: (scan) => (
      <Text>
        <span data-tooltip-id='global-tooltip' data-tooltip-content='CPU request (current → recommended)'>
          {formatFlow(formatCpu(scan.object?.allocations?.requests?.cpu), formatCpu(scan.recommended?.requests?.cpu))}
        </span>
      </Text>
    )
  },
  {
    key: 'cpuLimit',
    header: 'CPU limit',
    tooltip: 'CPU limit (current → recommended)',
    style: { width: 130, leftSpacing: 16 },
    sortValue: (scan) => unwrap(scan.object?.allocations?.limits?.cpu) ?? -1,
    render: (scan) => (
      <Text>
        <span data-tooltip-id='global-tooltip' data-tooltip-content='CPU limit (current → recommended)'>
          {formatFlow(formatCpu(scan.object?.allocations?.limits?.cpu), formatCpu(scan.recommended?.limits?.cpu))}
        </span>
      </Text>
    )
  },
  {
    key: 'memoryRequest',
    header: 'Memory request',
    tooltip: 'Memory request (current → recommended)',
    style: { width: 150, leftSpacing: 16 },
    sortValue: (scan) => unwrap(scan.object?.allocations?.requests?.memory) ?? -1,
    render: (scan) => (
      <Text>
        <span data-tooltip-id='global-tooltip' data-tooltip-content='Memory request (current → recommended)'>
          {formatFlow(formatMemory(scan.object?.allocations?.requests?.memory), formatMemory(scan.recommended?.requests?.memory))}
        </span>
      </Text>
    )
  },
  {
    key: 'memoryLimit',
    header: 'Memory limit',
    tooltip: 'Memory limit (current → recommended)',
    style: { width: 150, leftSpacing: 16 },
    sortValue: (scan) => unwrap(scan.object?.allocations?.limits?.memory) ?? -1,
    render: (scan) => (
      <Text>
        <span data-tooltip-id='global-tooltip' data-tooltip-content='Memory limit (current → recommended)'>
          {formatFlow(formatMemory(scan.object?.allocations?.limits?.memory), formatMemory(scan.recommended?.limits?.memory))}
        </span>
      </Text>
    )
  }
]

const TABLE_MIN_WIDTH = COLUMNS.reduce((sum, column) => sum + (Number(column.style.width) || 0), 0)

type SortDirection = 'asc' | 'desc'

const KrrReportView: React.FC<{ content: unknown }> = ({ content }) => {
  const [query, setQuery] = useState('')
  const [activeSeverities, setActiveSeverities] = useState<Set<string> | null>(null)
  const [sort, setSort] = useState<{ key: string; direction: SortDirection } | null>(null)

  const toggleSort = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const report = useMemo<KrrReport | null>(() => {
    if (content && typeof content === 'object') return content as KrrReport
    return null
  }, [content])

  const scans = useMemo(() => report?.scans ?? [], [report])

  const severities = useMemo(() => {
    const found = new Set<string>()
    scans.forEach((scan) => found.add(getSeverity(scan)))
    return Array.from(found).sort((a, b) => (SEVERITY_ORDER[a] ?? 99) - (SEVERITY_ORDER[b] ?? 99))
  }, [scans])

  const toggleSeverity = (severity: string) => {
    setActiveSeverities((prev) => {
      const next = new Set(prev ?? severities)
      if (next.has(severity)) next.delete(severity)
      else next.add(severity)
      return next
    })
  }

  const normalizedQuery = query.trim().toLowerCase()
  const visibleScans = scans
    .filter((scan) => {
      const severity = getSeverity(scan)
      if (activeSeverities !== null && !activeSeverities.has(severity)) return false
      if (!normalizedQuery) return true
      const haystack = `${scan.object?.namespace ?? ''} ${scan.object?.name ?? ''} ${scan.object?.kind ?? ''} ${scan.object?.container ?? ''}`.toLowerCase()
      return haystack.includes(normalizedQuery)
    })
    .sort((a, b) => {
      if (sort) {
        const column = COLUMNS.find((candidate) => candidate.key === sort.key)
        if (column?.sortValue) {
          const valueA = column.sortValue(a)
          const valueB = column.sortValue(b)
          const compared = valueA < valueB ? -1 : valueA > valueB ? 1 : 0
          return sort.direction === 'asc' ? compared : -compared
        }
      }
      const rankA = SEVERITY_ORDER[getSeverity(a)] ?? 99
      const rankB = SEVERITY_ORDER[getSeverity(b)] ?? 99
      return rankA - rankB
    })

  if (!report) {
    return (
      <Text textColor='grey' align='center'>
        This report could not be parsed as KRR JSON.
      </Text>
    )
  }

  return (
    <div>
      <Container justify='space-between' isFullWidth style={{ gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <Container style={{ gap: '18px' }}>
          {report.score !== undefined && <Text textStyle='description'>Score: {report.score}</Text>}
          {report.strategy?.name && <Text textStyle='description'>Strategy: {report.strategy.name}</Text>}
          <Text textStyle='description'>Objects: {scans.length}</Text>
          {!!report.errors?.length && (
            <Text textStyle='description' style={{ color: 'var(--tma-status-failed)' }}>
              Errors: {report.errors.length}
            </Text>
          )}
        </Container>
        <div className='search-no-glow' style={{ minWidth: '240px' }}>
          <TextField
            isSearchMode
            placeholder='Search namespace, name, kind…'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
            type='text'
            variant='default'
          />
        </div>
      </Container>

      <Container style={{ gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {severities.map((severity) => {
          const isActive = activeSeverities === null || activeSeverities.has(severity)
          return (
            <button
              key={severity}
              type='button'
              data-tooltip-id='global-tooltip'
              data-tooltip-content={severityDescription(severity, null)}
              onClick={() => toggleSeverity(severity)}
              style={{
                cursor: 'pointer',
                border: `1px solid ${severityColor(severity)}`,
                background: isActive ? severityColor(severity) : 'transparent',
                color: isActive ? 'var(--tma-panel-bg)' : severityColor(severity),
                borderRadius: '12px',
                padding: '2px 10px',
                fontSize: '12px',
                fontWeight: 600
              }}
            >
              {severityLabel(severity, null)}
            </button>
          )
        })}
      </Container>

      {visibleScans.length === 0 ? (
        <EmptyState imageName='emptyfolder' size='medium' title='No objects match' text='Try a different search or severity filter.' />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: `${TABLE_MIN_WIDTH}px` }}>
            <Table isTableFixed columnStyle={COLUMNS.map((column) => column.style)}>
              <TableRow>
                {COLUMNS.map((column) => {
                  const isSorted = sort?.key === column.key
                  const arrow = isSorted ? (sort?.direction === 'asc' ? ' ↑' : ' ↓') : ''
                  return (
                    <Text
                      key={column.key}
                      textStyle='description'
                      style={{ color: isSorted ? 'var(--tma-text-strong)' : 'var(--tma-text-muted)', fontWeight: 600, fontSize: '10px', letterSpacing: '0.02em', textTransform: 'uppercase' }}
                    >
                      <span
                        data-tooltip-id='global-tooltip'
                        data-tooltip-content={column.tooltip ?? column.header}
                        onClick={column.sortValue ? () => toggleSort(column.key) : undefined}
                        style={column.sortValue ? { cursor: 'pointer' } : undefined}
                      >
                        {column.header}
                        {arrow}
                      </span>
                    </Text>
                  )
                })}
              </TableRow>
              {visibleScans.map((scan, index) => (
                <TableRow key={`${scan.object?.namespace}-${scan.object?.name}-${scan.object?.container}-${index}`}>
                  {COLUMNS.map((column) => (
                    <React.Fragment key={column.key}>{column.render(scan)}</React.Fragment>
                  ))}
                </TableRow>
              ))}
            </Table>
          </div>
        </div>
      )}
    </div>
  )
}

export default KrrReportView
